import { toast } from '@atrvd/saas-ui'
import { ConfirmationDialog } from '@/components/admin/ConfirmationDialog'
import { useDeleteBankInstitution } from '@/hooks/useBanking'
import { MANUAL_BANK_COPY } from './manualBankCopy'
import type { ManualBankInScope } from './manualBanks.utils'

interface DeleteManualBankDialogProps {
  /** The bank to delete; null keeps the dialog closed. */
  bank: ManualBankInScope | null
  onClose: () => void
  /** Called after the bank was deleted, e.g. to leave its detail page. */
  onDeleted?: () => void
}

/**
 * Confirm and delete a whole manual bank: every live account and card of it
 * is soft-deleted (`bankAccountIds`) — at every facility, inactive ones
 * included, not only the listed ones; related transactions are kept. Leaving
 * the hidden ones would keep the bank alive under its name.
 */
export function DeleteManualBankDialog({ bank, onClose, onDeleted }: DeleteManualBankDialogProps) {
  const deleteInstitution = useDeleteBankInstitution()

  const confirm = () => {
    if (!bank) return
    deleteInstitution.mutate(
      bank.bankAccountIds,
      {
        onSuccess: () => {
          toast.success(`"${bank.displayName}" deleted`)
          onClose()
          onDeleted?.()
        },
        onError: (error) => toast.error(error.message),
      },
    )
  }

  return (
    <ConfirmationDialog
      open={bank !== null}
      onOpenChange={(open) => {
        if (!open && !deleteInstitution.isPending) onClose()
      }}
      title={bank ? `Delete ${bank.displayName}?` : MANUAL_BANK_COPY.deleteBankCta}
      description={MANUAL_BANK_COPY.deleteBankDescription}
      confirmText={MANUAL_BANK_COPY.deleteBankCta}
      cancelText="Cancel"
      variant="destructive"
      isLoading={deleteInstitution.isPending}
      onConfirm={confirm}
    />
  )
}
